import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authService } from '@/services/auth.service';
import { User, LoginDto, RegisterDto } from '@/types/user.types';
import i18n from '@/i18n';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (data: LoginDto) => Promise<{ user: User }>;
  register: (data: RegisterDto) => Promise<{ user: User }>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const parseStoredUser = (): User | null => {
  try {
    const raw = sessionStorage.getItem('user');
    return raw ? (JSON.parse(raw) as User) : null;
  } catch {
    return null;
  }
};

const clearStoredAuth = () => {
  sessionStorage.removeItem('justLoggedIn');
  sessionStorage.removeItem('user');
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [justLoggedIn, setJustLoggedIn] = useState(false);

  const checkAuth = async () => {
    const justLoggedInFlag = sessionStorage.getItem('justLoggedIn') === 'true' || justLoggedIn;
    if (justLoggedInFlag && user) {
      setLoading(false);
      return;
    }

    const cachedUser = parseStoredUser();
    if (cachedUser && !user) {
      setUser(cachedUser);
    }

    try {
      const userData = await authService.getCurrentUser();
      setUser(userData);
      sessionStorage.setItem('user', JSON.stringify(userData));
    } catch (error: any) {
      if (error.response?.status === 401) {
        if (!justLoggedInFlag) {
          setUser(null);
          clearStoredAuth();
        }
      } else {
        console.warn('AuthContext.checkAuth - erreur temporaire, session serveur conservée si valide');
        if (!cachedUser && !justLoggedInFlag) {
          setUser(null);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (data: LoginDto): Promise<{ user: User }> => {
    try {
      const response = await authService.login(data);

      sessionStorage.setItem('justLoggedIn', 'true');
      sessionStorage.setItem('user', JSON.stringify(response.user));
      setUser(response.user);
      setJustLoggedIn(true);
      setLoading(false);

      setTimeout(() => {
        sessionStorage.removeItem('justLoggedIn');
        setJustLoggedIn(false);
      }, 5000);

      return response;
    } catch (error: any) {
      clearStoredAuth();
      const errorMessage = error.response?.data?.error || error.message || i18n.t('auth.loginError');
      throw new Error(errorMessage);
    }
  };

  const register = async (data: RegisterDto): Promise<{ user: User }> => {
    try {
      const response = await authService.register(data);

      sessionStorage.setItem('justLoggedIn', 'true');
      sessionStorage.setItem('user', JSON.stringify(response.user));
      setUser(response.user);
      setJustLoggedIn(true);
      setLoading(false);

      setTimeout(() => {
        sessionStorage.removeItem('justLoggedIn');
        setJustLoggedIn(false);
      }, 5000);

      return response;
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.message || i18n.t('auth.registerError');
      throw new Error(errorMessage);
    }
  };

  const logout = async () => {
    await authService.logout();
    setUser(null);
    clearStoredAuth();
    window.location.href = '/login';
  };

  useEffect(() => {
    checkAuth();
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
